import { useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames/bind';
import { noop } from 'lodash/fp';
import { useDropFiles, useEventListener, useToggleState } from 'app/lib/hooks';
import { Icon, IconSize } from 'app/components/core/Icon';
import * as styles from './FileDropZone.css';

const cx = classnames.bind(styles);

export function FileDropZone({
    testId,
    className,
    onDrop = noop,
    placeholder,
    phrase,
    children,
    ...props
}) {
    const [isDragOver, toggleIsDragOver, , onDragLeave] = useToggleState(false);
    const classes = cx('base', className, { isDragOver });
    const dropRef = useRef();

    useDropFiles(dropRef, handleOnDropFiles);
    useEventListener(dropRef, 'dragenter', { callback: toggleIsDragOver });
    useEventListener(dropRef, 'dragleave', { callback: toggleIsDragOver });

    function handleOnDropFiles(files) {
        onDrop([...files]);
        onDragLeave();
    }

    return (
        <div data-testid={testId} className={classes} {...props}>
            <div className={styles.dropZone} data-testid="file-drop-zone" ref={dropRef}>
                <Icon className={styles.uploadIcon} variant="upload" size={IconSize.EXTRA_LARGE} />
                <div className={styles.messageBlock}>
                    {placeholder && <p>{placeholder}</p>}
                    {phrase && <p>{phrase}</p>}
                </div>
                {children}
                {isDragOver && <div className={styles.mask} />}
            </div>
        </div>
    );
}

FileDropZone.propTypes = {
    /**
     * Emit a `data-testid` prop on the root element of the component. Use this
     * to help automated testing tools find this particular component.
     */
    testId: PropTypes.string,

    /**
     * Function to call whenever the browser detects an ondrop event.
     * Argument is always a list of items.
     */
    onDrop: PropTypes.func,

    /**
     * Set additional CSS classes on the component to extend the default styles.
     */
    className: PropTypes.string,

    /**
     * Set placeholder text.
     */
    placeholder: PropTypes.string,

    /**
     * Set phrase text.
     */
    phrase: PropTypes.string,

    /**
     * Add custom content.
     */
    children: PropTypes.node
};
